import React from 'react';
import { User, Tenant } from '../types/user';
import { Conversation, AIProvider } from '../types/ai';
import { Assistant } from '../types/assistant';
import { MoreVertical, Trash2, Plus, Lock, Globe, Users } from 'lucide-react';

interface MobileSidebarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  tenant: Tenant;
  providers: AIProvider[];
  activeProvider: string | null;
  onProviderSelect: (providerId: string, modelId: string) => void;
  assistants: Assistant[];
  onAssistantSelect: (assistant: Assistant) => void;
  onCreateAssistant: () => void;
  conversations: Conversation[];
  onConversationSelect: (conversation: Conversation) => void;
  onDeleteConversation: (conversationId: string) => void;
  onNewChat: () => void;
  onProfileClick?: () => void;
}

export function MobileSidebarDrawer({
  isOpen,
  onClose,
  user,
  tenant,
  providers,
  activeProvider,
  onProviderSelect,
  assistants,
  onAssistantSelect,
  onCreateAssistant,
  conversations,
  onConversationSelect,
  onDeleteConversation,
  onNewChat,
  onProfileClick
}: MobileSidebarDrawerProps) {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[90]"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl z-[95] transform transition-transform duration-300 ease-in-out flex flex-col">
        {/* Header - Fixo */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <img src="/space-logo-black.png" alt="SPACE" className="h-6 object-contain" />
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Com scroll */}
        <div className="flex-1 overflow-y-auto bg-gray-50 min-h-0">
          {/* Seção de IAs */}
          <div className="bg-white p-4 border-b border-gray-200">
            <h2 className="text-sm font-medium text-gray-900 mb-4">Inteligência Artificial</h2>
            <div className="space-y-1">
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  onClick={() => {
                    // Selecionar o primeiro modelo disponível do provedor
                    const defaultModel = provider.models[0]?.id || '';
                    onProviderSelect(provider.id, defaultModel);
                    onClose();
                  }}
                  className={`w-full flex items-center justify-start text-sm font-medium h-10 px-3 rounded-lg transition-colors ${
                    activeProvider === provider.id 
                      ? 'bg-gray-100 text-gray-900' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    {provider.id === 'openai' ? (
                      <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-5 h-5 object-contain" />
                    ) : provider.id === 'anthropic' ? (
                      <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-5 h-5 object-contain" />
                    ) : provider.id === 'google' ? (
                      <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-5 h-5 object-contain" />
                    ) : (
                      provider.logo
                    )}
                  </div>
                  {provider.name}
                </button>
              ))}
            </div>
          </div>

          {/* Seção de Agentes */}
          <div className="bg-white p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-gray-900">Agentes</h2>
              <button
                onClick={() => {
                  onCreateAssistant();
                  onClose();
                }}
                className="flex items-center space-x-1 text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
              >
                <Plus className="w-3 h-3" />
                <span>Criar</span>
              </button>
            </div>
            
            <div className={`space-y-1 ${assistants.length > 10 ? 'max-h-96 overflow-y-auto' : ''}`}>
              {assistants.length > 0 ? (
                assistants.map((assistant) => (
                  <button
                    key={assistant.id}
                    onClick={() => {
                      onAssistantSelect(assistant);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between text-sm font-medium h-10 px-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center min-w-0">
                      <div className="w-6 h-6 flex items-center justify-center mr-3 text-sm flex-shrink-0">
                        {assistant.avatar || '🤖'}
                      </div>
                      <span className="truncate text-left">{assistant.name}</span>
                    </div>
                    {assistant.visibility && (
                      <div className="flex-shrink-0 ml-2">
                        {assistant.visibility === 'private' && (
                          <Lock className="w-3 h-3 text-gray-400" />
                        )}
                        {assistant.visibility === 'team' && (
                          <Globe className="w-3 h-3 text-gray-400" />
                        )}
                        {assistant.visibility === 'shared' && (
                          <Users className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    )}
                  </button>
                ))
              ) : (
                <div className="text-xs text-gray-500 text-center py-4">
                  Nenhum agente ainda
                </div>
              )}
            </div>
          </div>

          {/* Seção de Conversas */}
          <div className="bg-white flex-1 flex flex-col min-h-0">
            <div className="flex-shrink-0 p-4 pb-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">Todas as Conversas</h3>
                {activeProvider && (
                  <button
                    onClick={() => {
                      onNewChat();
                      onClose();
                    }}
                    className="flex items-center space-x-1 text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Nova</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0">
              <div className="space-y-1">
                {conversations.length > 0 ? (
                  conversations.map((conversation) => (
                    <div key={conversation.id} className="group relative flex items-center">
                      <button
                        onClick={() => {
                          onConversationSelect(conversation);
                          onClose();
                        }}
                        className="w-full flex items-center justify-start text-xs h-10 px-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors pr-10"
                      >
                        <div className="w-4 h-4 flex items-center justify-center mr-3">
                          {conversation.aiId === 'openai' ? (
                            <img src="/gpt-logo.png?v=1" alt="GPT Logo" className="w-3 h-3 object-contain" />
                          ) : conversation.aiId === 'anthropic' ? (
                            <img src="/claude-logo.png?v=1" alt="Claude Logo" className="w-3 h-3 object-contain" />
                          ) : conversation.aiId === 'google' ? (
                            <img src="/gemini-logo.png?v=1" alt="Gemini Logo" className="w-3 h-3 object-contain" />
                          ) : (
                            <span className="text-xs">AI</span>
                          )}
                        </div>
                        <div className="flex-1 flex items-center justify-between min-w-0">
                          <span className="truncate text-left">{conversation.title}</span>
                          {conversation.messageCount !== undefined && conversation.messageCount > 0 && (
                            <span className="ml-2 text-[10px] text-gray-400 flex-shrink-0">
                              {conversation.messageCount}
                            </span>
                          )}
                        </div>
                      </button>

                      {/* Menu de 3 pontinhos */}
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === conversation.id ? null : conversation.id);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all"
                          title="Mais opções"
                        >
                          <MoreVertical className="w-3 h-3" />
                        </button>

                        {/* Menu Dropdown */}
                        {openMenuId === conversation.id && (
                          <>
                            {/* Backdrop para fechar o menu */}
                            <div
                              className="fixed inset-0"
                              style={{ zIndex: 9998 }}
                              onClick={() => setOpenMenuId(null)}
                            />

                            {/* Menu */}
                            <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-xl border border-gray-200 py-1" style={{ zIndex: 9999 }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuId(null);
                                  onDeleteConversation(conversation.id);
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                              >
                                <Trash2 className="w-3 h-3" />
                                <span>Excluir</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-500 text-center py-8">
                    Nenhuma conversa ainda
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixo */}
        <div className="flex-shrink-0 border-t bg-white">
          {/* Perfil do Usuário */}
          <div className="p-3">
            <button
              onClick={() => {
                if (onProfileClick) {
                  onProfileClick();
                  onClose();
                }
              }}
              className="w-full text-left p-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium text-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{user.name}</p>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
